"use client";

import { useState, useEffect } from "react";
import type { BootPhase } from "@/hooks/useBootSequence";
import type { ZoneState } from "@/hooks/useZoneState";
import { useLetterGlitch } from "@/hooks/useLetterGlitch";
import { Magnetic } from "@/components/ui/Magnetic";
import { EphemeralText } from "@/components/ui/EphemeralText";
import { type DomainId, getDomainConfig } from "@/data/domains";

// Transition timing
const TRANSITION_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_DURATION = "400ms";
const TRANSITION_DURATION_MS = 400;

// Stable empty reference so referential equality holds across renders when a
// domain declares no subtitles (prevents subtitlesMatch from flickering).
const EMPTY_SUBTITLES: readonly string[] = Object.freeze([]);

// =============================================================================
// BOOT IDENTITY LAYER
// =============================================================================
// Shows hero lockup (Vincent Ngu) at the top
// Three states:
//   - idle: name + subtitle lines from active domain (~96px)
//   - peek: subtitles animate out, name stays smaller (~38px)
//   - read: hero hidden, replaced by orb nav strip (~32px)
//
// Hero copy (subtitles) is driven by the active domain's `hero` config in
// src/data/domains.ts. Domains without a `hero` entry render name only.
//
// Crossfade model: per-slot diff. When the active domain's subtitle array
// changes, each slot compares its old (displayed) vs new (target) text. Only
// slots whose text differs animate. Matching slots stay put. This keeps the
// eye focused on what actually changed (e.g. Works ↔ Contact only swaps L3).

interface BootIdentityLayerProps {
  phase: BootPhase;
  phaseProgress: number;
  isComplete: boolean;
  // Zone state props
  zoneState: ZoneState;
  z1Height: number;
  isTinyViewport?: boolean;  // < 500px - hide subtitles even in idle
  isCrampedViewport?: boolean;  // Square-ish or short - hide subtitles to avoid Z2/Z3 collision
  isNarrowViewport?: boolean;  // < 400px wide - compact name, smaller subtitles
  activeDomain?: DomainId;   // Drives which hero copy to show (from domain registry)
  crtEnabled?: boolean;      // CRT effects (trapezoid, chromatic aberration)
  hideSubtitles?: boolean;   // Fade out subtitles only (e.g. when content is scrolled)
  dimHero?: boolean;         // Fade out entire hero (name + subtitles). Used by
                             // blog to prevent the name bleeding past z1 from
                             // colliding with the sticky pill bar below. Name
                             // overflows its 56px content-domain z1 at certain
                             // viewport sizes; dimming is cleaner than fighting
                             // z-index stacking (BlogOverlay z-14 < Hero z-15).
  // Legacy props (kept for compat during migration)
  isPeeking?: boolean;
  peekProgress?: number;
}

// =============================================================================
// SubtitleLine - single subtitle row with letter glitch + CRT styling
// =============================================================================
// Extracted so a variable number of lines can be rendered without breaking the
// Rules of Hooks (each instance owns its own useLetterGlitch call).

interface SubtitleLineProps {
  text: string;
  index: number;          // 0 = emphasized (bold + magnetic ephemeral)
  revealOpacity: number;  // 0→1 during boot reveal
  crtEnabled: boolean;
}

function SubtitleLine({ text, index, revealOpacity, crtEnabled }: SubtitleLineProps) {
  const { text: glitched } = useLetterGlitch(text, {
    idleGlitch: true,
    intervalMultiplier: 0.5,
    enabled: crtEnabled,
  });

  const isEmphasized = index === 0;
  const marginTop = index === 0 ? "mt-2" : "mt-1";
  const baseClasses = `text-[clamp(0.75rem,1.8vw,1.05rem)] text-[rgba(var(--foreground-rgb),0.5)] tracking-[0.08em] leading-[1.6] ${marginTop}`;
  const emphasisClasses = isEmphasized ? " font-bold text-balance" : "";

  return (
    <p
      className={baseClasses + emphasisClasses}
      style={{
        opacity: revealOpacity,
        transform: crtEnabled
          ? `perspective(400px) rotateX(12deg) translateY(${(1 - revealOpacity) * 8}px)`
          : `translateY(${(1 - revealOpacity) * 8}px)`,
        transformOrigin: "bottom center",
        textShadow: crtEnabled ? "var(--crt-text-shadow)" : "none",
      }}
    >
      {isEmphasized ? <EphemeralText hoverOpacity={0.5}>{glitched}</EphemeralText> : glitched}
    </p>
  );
}

// =============================================================================
// SubtitleSlot - per-slot crossfade layer
// =============================================================================
// Diffs displayedText (what's committed) against targetText (the new domain's
// line at this slot). Matching slots render one layer at opacity 1. Differing
// slots stack two layers and crossfade — outgoing fades to 0, incoming fades
// from 0 to 1 (via FadeInLayer's mount animation). Once the parent commits the
// swap, displayedText catches up to targetText, same becomes true, overlay
// unmounts.

interface SubtitleSlotProps {
  slotIndex: number;
  displayedText: string | undefined;
  targetText: string | undefined;
  revealOpacity: number;
  crtEnabled: boolean;
}

function SubtitleSlot({ slotIndex, displayedText, targetText, revealOpacity, crtEnabled }: SubtitleSlotProps) {
  const same = displayedText === targetText;
  if (!displayedText && !targetText) return null;

  // Grid stack: both layers share `grid-area: 1 / 1`, so the cell sizes to the
  // WIDER of the two texts. Previously used position:absolute + inset:0 which
  // locked the incoming overlay to the outgoing text's width — if the new line
  // was longer (e.g. Contact → Works on L3), it wrapped mid-transition and
  // snapped back when state committed. Grid stacking lets the incoming line
  // render at its natural width from the first frame.
  return (
    <div className="grid">
      {/* Base layer: currently-displayed text. Fades to 0 during a diff; flips
          back to 1 once `displayed` catches up to `target`. */}
      {displayedText && (
        <div
          style={{
            gridArea: "1 / 1",
            opacity: same ? 1 : 0,
            transition: `opacity ${TRANSITION_DURATION} ${TRANSITION_EASE}`,
          }}
        >
          <SubtitleLine
            text={displayedText}
            index={slotIndex}
            revealOpacity={revealOpacity}
            crtEnabled={crtEnabled}
          />
        </div>
      )}
      {/* Overlay: mounted only during a diff, fades 0 → 1 via FadeInLayer's
          rAF mount animation. Shares the same grid cell as the base. */}
      {!same && targetText && (
        <FadeInLayer>
          <SubtitleLine
            text={targetText}
            index={slotIndex}
            revealOpacity={revealOpacity}
            crtEnabled={crtEnabled}
          />
        </FadeInLayer>
      )}
    </div>
  );
}

// Mounts at opacity 0, flips to 1 on the next frame so CSS transition fires.
// Used for the incoming half of a slot crossfade. Always grid-stacked at 1/1.
function FadeInLayer({ children }: { children: React.ReactNode }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      style={{
        gridArea: "1 / 1",
        opacity: entered ? 1 : 0,
        transition: `opacity ${TRANSITION_DURATION} ${TRANSITION_EASE}`,
      }}
    >
      {children}
    </div>
  );
}

export function BootIdentityLayer({
  phase,
  phaseProgress,
  isComplete,
  zoneState,
  z1Height,
  isTinyViewport = false,
  isCrampedViewport = false,
  isNarrowViewport = false,
  activeDomain = "projects",
  crtEnabled = true,
  hideSubtitles = false,
  dimHero = false,
  isPeeking = false,
  peekProgress = 0,
}: BootIdentityLayerProps) {
  // Vicente easter egg: swap Vincent → Vicente every 8s for 150ms
  const [isVicente, setIsVicente] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setIsVicente(true);
      setTimeout(() => setIsVicente(false), 150);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Letter glitch for name (CRT aesthetic)
  const baseName = isVicente ? "Vicente Ngu" : "Vincent Ngu";
  const { text: glitchedName } = useLetterGlitch(baseName, {
    idleGlitch: true,
    intervalMultiplier: 0.5,
    enabled: crtEnabled,
  });

  // Per-slot crossfade: track displayed subtitles separately from target.
  // When they diverge, diffing slots animate individually. After
  // TRANSITION_DURATION_MS, displayed catches up to target and overlays unmount.
  const targetSubtitles = (getDomainConfig(activeDomain).hero?.subtitles ?? EMPTY_SUBTITLES) as readonly string[];
  const [displayedSubtitles, setDisplayedSubtitles] = useState<readonly string[]>(targetSubtitles);

  const subtitlesMatch =
    targetSubtitles.length === displayedSubtitles.length &&
    targetSubtitles.every((t, i) => t === displayedSubtitles[i]);

  useEffect(() => {
    if (subtitlesMatch) return;
    const t = setTimeout(() => setDisplayedSubtitles(targetSubtitles), TRANSITION_DURATION_MS);
    return () => clearTimeout(t);
  }, [subtitlesMatch, targetSubtitles]);

  // Pre-ring: ghost-render the H1 so it lands in the LCP paint window.
  // The StarLoader (z-[9999]) visually covers it during loader phase, and void
  // is a 1.8s window where a faint ghost is acceptable. Ring phase runs the
  // full reveal as before.
  const isPreRing = phase !== "ring";

  // Calculate opacity for name/subtitle reveal based on boot progress.
  // All text comes in together — single beat at 0-15% of ring phase.
  const nameOpacity = isPreRing ? 0.15 : Math.min(1, phaseProgress / 0.15);
  const subtitleRevealOpacity = isPreRing ? 0 : nameOpacity;

  // Glow effect: strong during early reveal, settles down. Suppress entirely
  // during ghost paint so we don't get a glowing pre-boot ghost.
  const glowIntensity = isPreRing ? 0 : nameOpacity < 0.5 ? 40 - nameOpacity * 60 : 20;
  const glowOpacity = isPreRing ? 0 : nameOpacity < 0.5 ? 0.8 - nameOpacity : 0.2;

  // State-based styling
  const isIdle = zoneState === "idle";
  const isPeek = zoneState === "peek";
  const isRead = zoneState === "read";

  // Slot count covers both displayed and target so a shrinking or growing
  // domain's lines can animate through the transition window.
  const slotCount = Math.max(displayedSubtitles.length, targetSubtitles.length);
  const hasSubtitleContent = slotCount > 0;

  // Subtitle block visibility. Hidden when:
  //   - not idle (peek/read collapse the block)
  //   - viewport is too small for the extra lines
  //   - no slots have content (e.g. sitting in a content domain with no hero)
  const showSubtitles =
    isIdle && !isTinyViewport && !isCrampedViewport && hasSubtitleContent;
  const subtitleMaxHeight = showSubtitles
    ? isNarrowViewport
      ? "80px"
      : "100px"
    : "0px";
  const subtitleBlockOpacity = showSubtitles && !hideSubtitles ? 1 : 0;

  // Name font size: smaller on peek OR tiny/cramped/narrow viewports
  const useCompactName = isPeek || isTinyViewport || isCrampedViewport || isNarrowViewport;
  const nameFontSize = useCompactName
    ? "clamp(1.2rem, 4vw, 1.8rem)"
    : "clamp(2.5rem, 8vw, 4rem)";

  // Container padding
  const containerPadding =
    isIdle && !isTinyViewport && !isCrampedViewport
      ? isNarrowViewport
        ? "16px"
        : "clamp(16px, 3vh, 28px)"
      : "22px";

  // Hero content opacity (hidden on read, dimmed when content domain asks for it).
  // dimHero fades to a faint ghost (0.18) rather than 0 — enough that the pill
  // bar wins the contrast fight, but the name still anchors the top of frame.
  const heroOpacity = isRead ? 0 : dimHero ? 0.18 : 1;

  // Name is interactive on the default landing (projects); other domains let
  // pointer events pass through to the overlay content behind.
  const nameInteractive = !isRead && activeDomain === "projects";

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[15] flex flex-col items-center justify-start text-center pointer-events-none overflow-visible"
      style={{
        height: z1Height,
        paddingTop: containerPadding,
        opacity: heroOpacity,
        transition: `all ${TRANSITION_DURATION} ${TRANSITION_EASE}`,
      }}
    >
      <Magnetic
        strength={0.08}
        tiltStrength={4}
        padding={200}
        className={nameInteractive ? "pointer-events-auto" : "pointer-events-none"}
      >
        {/* Name */}
        <h1
          className="font-bold tracking-tight text-[rgba(var(--foreground-rgb),0.85)] mb-0 font-headline"
          style={{
            fontSize: nameFontSize,
            opacity: nameOpacity,
            textShadow: crtEnabled
              ? `-1px 0 rgba(255, 0, 80, 0.4), 1px 0 rgba(0, 200, 255, 0.4), 0 0 ${glowIntensity}px rgba(255, 180, 100, ${glowOpacity})`
              : `0 0 ${glowIntensity}px rgba(255, 180, 100, ${glowOpacity})`,
            transform: crtEnabled
              ? `perspective(400px) rotateX(10deg) translateY(${(1 - nameOpacity) * 12}px)`
              : `translateY(${(1 - nameOpacity) * 12}px)`,
            transformOrigin: "bottom center",
            transition: `font-size ${TRANSITION_DURATION} ${TRANSITION_EASE}, text-shadow 300ms ease, transform 300ms ease`,
          }}
        >
          <EphemeralText hoverOpacity={0.5}>{glitchedName}</EphemeralText>
        </h1>

        {/* Subtitle block — per-slot crossfade. Each slot diffs old vs new
            text and animates only when they differ. Block-level max-height
            handles the zero-slot collapse (e.g. Works → Manifesto). */}
        <div
          className="px-4"
          style={{
            maxHeight: subtitleMaxHeight,
            opacity: subtitleBlockOpacity,
            overflow: showSubtitles ? "visible" : "hidden",
            transition: `max-height ${TRANSITION_DURATION} ${TRANSITION_EASE}, opacity ${TRANSITION_DURATION} ${TRANSITION_EASE}`,
          }}
        >
          {Array.from({ length: slotCount }).map((_, i) => (
            <SubtitleSlot
              key={i}
              slotIndex={i}
              displayedText={displayedSubtitles[i]}
              targetText={targetSubtitles[i]}
              revealOpacity={subtitleRevealOpacity}
              crtEnabled={crtEnabled}
            />
          ))}
        </div>
      </Magnetic>
    </div>
  );
}
